import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  type SeasonStats,
  BATTER_COLUMNS, PITCHER_COLUMNS, PITCHER_POSITIONS,
  getStatDisplay, getSabPtsDisplay,
} from '@/components/mlb-beta/statsTableDefs';

interface Props {
  playerId: string;
  position: string;
  projectedTrashPoints: number;
}

const PlayerStatsExpand = ({ playerId, position, projectedTrashPoints }: Props) => {
  const [stats, setStats] = useState<SeasonStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statView, setStatView] = useState<'season' | 'pergame'>('season');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('player_season_stats')
        .select('games_played, at_bats, hits, runs_scored, doubles, triples, home_runs, rbi, walks_drawn, strikeouts, stolen_bases, caught_stealing, gidp, pitcher_wins, pitcher_strikeouts, saves, innings_pitched, earned_runs, walks_allowed, home_runs_allowed, is_pitcher, blown_saves')
        .eq('player_id', playerId)
        .order('season', { ascending: false })
        .limit(1)
        .maybeSingle();
      console.log('[PlayerStatsExpand] player_id:', playerId, 'data:', data, 'error:', error);
      console.log('[PlayerStatsExpand] raw JSON:', JSON.stringify(data));
      setStats(data as SeasonStats | null);
      setLoading(false);
    };
    fetchData();
  }, [playerId]);

  if (loading) {
    return (
      <div className="px-2 sm:px-3 pb-1 sm:pb-3">
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="px-2 sm:px-3 pb-1 sm:pb-3 text-xs text-muted-foreground text-center py-2">
        No season stats available
      </div>
    );
  }

  const isPitcher = stats.is_pitcher || PITCHER_POSITIONS.includes(position);
  const columns = isPitcher ? PITCHER_COLUMNS : BATTER_COLUMNS;
  const sabPts = getSabPtsDisplay(projectedTrashPoints, statView, stats.games_played || 0);

  return (
    <div className="px-2 sm:px-3 pb-1 sm:pb-3">
      <div className="flex items-center gap-1 mb-1">
        <Button
          size="sm"
          variant={statView === 'season' ? 'secondary' : 'ghost'}
          className="h-5 text-[9px] px-2"
          onClick={(e) => { e.stopPropagation(); setStatView('season'); }}
        >
          Season
        </Button>
        <Button
          size="sm"
          variant={statView === 'pergame' ? 'secondary' : 'ghost'}
          className="h-5 text-[9px] px-2"
          onClick={(e) => { e.stopPropagation(); setStatView('pergame'); }}
        >
          Per Game
        </Button>
      </div>
      <div className="rounded border border-border overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="bg-muted/50 text-muted-foreground">
              {columns.map(c => (
                <th key={c.key} className="text-center px-1.5 py-1 font-semibold whitespace-nowrap">{c.label}</th>
              ))}
              <th className="text-center px-1.5 py-1 font-bold text-orange-400 whitespace-nowrap border-l border-border/50">SAB PTS</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border/50">
              {columns.map(c => (
                <td key={c.key} className="text-center px-1.5 py-1.5 text-foreground tabular-nums">
                  {getStatDisplay(c, stats, statView)}
                </td>
              ))}
              <td className={cn(
                "text-center px-1.5 py-1.5 font-bold tabular-nums border-l border-border/50",
                sabPts !== '-' ? "text-orange-400" : "text-muted-foreground"
              )}>
                {sabPts}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlayerStatsExpand;
